import { CardContent, Card, Typography, Button } from "@mui/material";
import { Box, Stack } from "@mui/system";
import logoSrc from "assets/icons/logo_light_horisontal.png";
import backgroundImageSrc from "assets/images/auth_bg.jpg";

import { useNavigate, useLocation } from "react-router-dom";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import { ERequestStatus } from "store/enums/index.enum";
import {
  VerifyCodeForgotPassword,
  ResendCodeForgotPassword,
} from "store/slicers/users";
import {
  type FormEvent,
  type SetStateAction,
  useEffect,
  useState,
} from "react";
import OTPInput from "shared/ui/OTPInput";

const RESEND_TIMEOUT = 60;

const VerificationCodePage = () => {
  const dispatch = useAsyncDispatch();
  const navigate = useNavigate();

  const [secondsLeft, setSecondsLeft] = useState(RESEND_TIMEOUT);

  const location = useLocation();

  const email = location.state?.email;

  /**
   * We don't use RHF here because the OTPInput is split into 6 small inputs
   *  and the library doesn't handle controls like that well
   */
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!email) {
      navigate("/reset-password");
    }
  }, [email, navigate]);

  const handleChangeCode = (value: SetStateAction<string>) => {
    setError("");
    setOtpCode(value);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (otpCode.length !== 6) {
      setError("Code must be 6 characters");

      return;
    }

    if (!email) return;

    const payload = {
      code: otpCode,
      email,
    };

    const { meta } = await dispatch(VerifyCodeForgotPassword(payload));

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }

    navigate("/new-password", {
      state: {
        email,
        code: otpCode,
      },
    });
  };

  const handleResend = async () => {
    if (!email) return;

    const payload = { email };

    const { meta } = await dispatch(ResendCodeForgotPassword(payload));

    if (meta.requestStatus === ERequestStatus.FULFILLED) {
      setSecondsLeft(RESEND_TIMEOUT);
    }
  };

  useEffect(() => {
    if (secondsLeft <= 0) return;

    const timer = setTimeout(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [secondsLeft]);

  return (
    <Box
      component="main"
      sx={{
        height: "100vh",
        backgroundImage: `url(${backgroundImageSrc})`,
        backgroundSize: "cover",
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    >
      <Card
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          p: { xs: 0, sm: 2 },
          width: { xs: "95%", md: "40%", lg: "35%", xl: "25%" },
        }}
      >
        <CardContent
          sx={{
            p: "20px",
            py: "100px",
            width: "100%",
            paddingTop: 2,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              width: "100%",
              justifyContent: "center",
              height: 70,
              mb: 2,
            }}
          >
            <img src={logoSrc} alt="SatisfAI logo" />
          </Box>

          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography variant="h6">Enter verification code</Typography>
            <Typography variant="body2" color="text.secondary">
              We’ve sent a verification code to your email.
            </Typography>
          </Stack>

          <form noValidate onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <OTPInput
                name="otp-code"
                size="small"
                separator={<span>-</span>}
                length={6}
                inputProps={{
                  inputMode: "numeric",
                }}
                value={otpCode}
                onChange={handleChangeCode}
                error={!!error}
                helperText={error}
                onlyNumbers
              />
            </Stack>

            <Button
              fullWidth
              size="large"
              sx={{ mt: 3 }}
              type="submit"
              variant="contained"
              disabled={otpCode.length !== 6}
            >
              Verify
            </Button>

            <Button
              fullWidth
              size="large"
              sx={{ mt: 1 }}
              type="button"
              variant="text"
              disabled={secondsLeft > 0}
              onClick={handleResend}
            >
              {secondsLeft > 0
                ? `Resend code in ${secondsLeft}s`
                : "Resend code"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

VerificationCodePage.dispayName = "VerificationCodePage";

export default VerificationCodePage;
